import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchAdminContent,
  saveAdminSection,
  getAdminToken,
  setAdminToken,
  isAdminSessionError,
} from '../content/contentApi';
import { normalizeServicePage } from '../content/servicePageTemplate';
import TemplateServicePage from '../components/services/TemplateServicePage';
import EditorTopBar from '../components/services/editable/EditorTopBar';
import './Admin.css';

/**
 * Dedicated full-viewport route for inline WYSIWYG editing of one service
 * page (registered in AdminApp.jsx as service-pages/:slug/edit). This is
 * intentionally OUTSIDE the tabbed AdminDashboard layout — WYSIWYG editing
 * needs the whole viewport, not a modal — so it fetches/saves the
 * `servicePages` section directly, reusing the same contentApi helpers
 * (and the same 401 → session-expired handling) as every other editor.
 */
export default function ServicePageEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [servicePages, setServicePages] = useState(null);
  const [draft, setDraft] = useState(null); // { data, styles, title }
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(false);
  const [status, setStatus] = useState('');

  const handleSessionExpired = useCallback(() => {
    setAdminToken(null);
    navigate('/admin/login?reason=session', { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const api = await fetchAdminContent();
      const sp =
        api.servicePages && typeof api.servicePages === 'object'
          ? api.servicePages
          : { list: [], pages: {} };
      const listEntry = (sp.list || []).find((s) => s.slug === slug);
      const doc = normalizeServicePage(sp.pages?.[slug], slug, listEntry?.title);
      setServicePages({ list: sp.list || [], pages: sp.pages || {} });
      setDraft({ data: doc.data, styles: doc.styles, title: doc.title });
      setDirty(false);
    } catch (e) {
      if (isAdminSessionError(e)) {
        handleSessionExpired();
        return;
      }
      setError(e.message || 'Failed to load this page.');
    } finally {
      setLoading(false);
    }
  }, [slug, handleSessionExpired]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const handleChange = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!draft || !servicePages) return;
    setSaving(true);
    setError('');
    try {
      const now = new Date().toISOString();
      const title = draft.data?.hero?.title || draft.title;
      const exists = servicePages.list.some((s) => s.slug === slug);
      const nextList = exists
        ? servicePages.list.map((s) => (s.slug === slug ? { ...s, title, updatedAt: now } : s))
        : [...servicePages.list, { slug, title, createdAt: now, updatedAt: now }];
      const nextPages = {
        ...servicePages.pages,
        [slug]: {
          slug,
          title,
          data: draft.data,
          styles: draft.styles,
          createdAt: servicePages.pages[slug]?.createdAt || now,
          updatedAt: now,
        },
      };
      const nextSection = { list: nextList, pages: nextPages };
      await saveAdminSection('servicePages', nextSection);
      setServicePages(nextSection);
      setDirty(false);
      setStatus('Saved · the public page now matches this.');
    } catch (e) {
      if (isAdminSessionError(e)) {
        handleSessionExpired();
        return;
      }
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !draft) {
    return (
      <div className="admin-root">
        <div className="admin-loading">
          <p>Loading page editor…</p>
        </div>
      </div>
    );
  }

  const statusLabel = saving ? 'Saving…' : dirty ? 'Unsaved changes' : status || 'No changes yet';

  return (
    <div className="admin-root">
      <EditorTopBar
        title={draft.data?.hero?.title || draft.title}
        preview={preview}
        onTogglePreview={() => setPreview((p) => !p)}
        onSave={handleSave}
        saving={saving}
        statusLabel={statusLabel}
        onBack={() => navigate('/admin')}
      />
      {error ? <p className="admin-error" style={{ padding: '10px 20px', margin: 0 }}>{error}</p> : null}
      <TemplateServicePage
        data={draft.data}
        styles={draft.styles}
        editable
        preview={preview}
        onChange={handleChange}
        currentSlug={slug}
      />
    </div>
  );
}
